import { useEffect, useState } from 'react';
import {
    Button,
    Select,
    SelectItem,
    Card,
    CardBody,
    CardHeader,
    Calendar,
    DateValue,
    CircularProgress
} from "@heroui/react";
import { format, setHours, setMinutes } from 'date-fns';
import { Trash, Plus, Clock, Check, X, Info } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { usePlannedVisitsCheck } from 'shared/components/rental/hooks/usePlannedVisitsCheck';
import { PlannedVisitsWarningModal } from 'shared/components/rental/components/PlannedVisitsWarningModal';
import type { CandidateInfo } from 'components/candidates/types';

interface TimeslotRow {
    id: number;
    availableStartTime: string;
    availableEndTime: string;
}

interface ImprovedTimeslotManagerProps {
    unitId: string;
    /**
     * Wordt aangeroepen wanneer deze component een eigen overlay opent/sluit. Een
     * parent die zelf een modal is (TimeslotModal) kan zich dan tijdelijk verbergen:
     * een HeroUI Modal en een Radix Dialog over elkaar botsen op focus trapping.
     */
    onBlockingOverlayChange?: (isOpen: boolean) => void;
}

const ImprovedTimeslotManager = ({ unitId, onBlockingOverlayChange }: ImprovedTimeslotManagerProps) => {
    const [selectedDate, setSelectedDate] = useState<DateValue | null>(today(getLocalTimeZone()));
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('17:00');
    const [timeslots, setTimeslots] = useState<any>([]);
    const { session } = useSession();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState<number | null>(null);
    const { check: checkPlannedVisits } = usePlannedVisitsCheck();
    const [pendingDeletion, setPendingDeletion] = useState<{ slot: TimeslotRow; visits: CandidateInfo[] } | null>(null);

    // Fetch existing timeslots
    const fetchExistingTimeslots = async () => {
        try {
            setIsLoading(true);
            const response = await fetchWithAuth(
                `${API_URL}/calendar/unit/${unitId}`,
                { method: 'GET' },
                session
            );

            if (response.ok) {
                const existingSlots = await response.json();
                setTimeslots(existingSlots);
            }
        } catch (error) {
            console.error('Failed to fetch timeslots:', error);
            toast.error('Fout bij ophalen tijdslots');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (unitId) {
            fetchExistingTimeslots();
        }
    }, [unitId, session]);

    useEffect(() => {
        onBlockingOverlayChange?.(!!pendingDeletion);
    }, [pendingDeletion, onBlockingOverlayChange]);

    // Generate time options from 8:00 to 22:00
    const timeOptions = Array.from({ length: 57 }, (_, i) => {
        const hours = Math.floor(i / 4) + 8;
        const minutes = (i % 4) * 15;
        return format(setMinutes(setHours(new Date(), hours), minutes), 'HH:mm');
    });

    // Auto-save when adding a timeslot - no intermediate state
    const handleAddTimeslot = async () => {
        if (!selectedDate) return;

        setIsSaving(true);
        try {
            const baseDate = new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day);

            const [startHour, startMinute] = startTime.split(':').map(Number);
            const [endHour, endMinute] = endTime.split(':').map(Number);

            const startDateTime = new Date(baseDate);
            startDateTime.setHours(startHour, startMinute, 0, 0);

            const endDateTime = new Date(baseDate);
            endDateTime.setHours(endHour, endMinute, 0, 0);

            const newTimeslot = {
                unitId,
                startTime: startDateTime.toISOString(),
                endTime: endDateTime.toISOString()
            };

            const response = await fetchWithAuth(`${API_URL}/calendar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify([newTimeslot]),
            }, session);

            if (response.ok) {
                await fetchExistingTimeslots();
                toast.success('Tijdslot toegevoegd!');
                
                // Reset form
                setSelectedDate(null);
                setStartTime('09:00');
                setEndTime('17:00');
            } else {
                toast.error('Fout bij toevoegen tijdslot');
            }
        } catch (error) {
            console.error('Failed to save timeslot:', error);
            toast.error('Fout bij toevoegen tijdslot');
        } finally {
            setIsSaving(false);
        }
    };

    const deleteTimeslot = async (timeslotId: number) => {
        setIsDeleting(timeslotId);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/calendar/${timeslotId}`,
                { method: 'DELETE' },
                session
            );

            if (response.ok) {
                await fetchExistingTimeslots();
                toast.success('Tijdslot verwijderd!');
            } else {
                throw new Error(`Delete timeslot failed: ${response.status}`);
            }
        } catch (error) {
            console.error('Failed to delete timeslot:', error);
            toast.error('Fout bij verwijderen tijdslot');
            Sentry.captureException(error, {
                tags: { feature: 'timeslots', method: 'deleteTimeslot' },
                extra: { unitId, timeslotId },
            });
        } finally {
            setIsDeleting(null);
        }
    };

    /**
     * Een tijdslot verwijderen haalt enkel de beschikbaarheid weg — een bezoek dat
     * binnen dat tijdslot geboekt is blijft gewoon staan en de kandidaat hoort niets.
     * Daarom eerst controleren en waarschuwen.
     */
    const handleDeleteTimeslot = async (slot: TimeslotRow) => {
        setIsDeleting(slot.id);
        const visits = await checkPlannedVisits(unitId, {
            from: slot.availableStartTime,
            to: slot.availableEndTime,
        });
        setIsDeleting(null);

        // visits === null: de controle zelf faalde (toast is al getoond) — een geldige
        // verwijdering mag daar niet op blokkeren.
        if (visits && visits.length > 0) {
            setPendingDeletion({ slot, visits });
            return;
        }

        await deleteTimeslot(slot.id);
    };

    const handleConfirmDeletion = async () => {
        if (!pendingDeletion) return;
        const { slot } = pendingDeletion;
        setPendingDeletion(null);
        await deleteTimeslot(slot.id);
    };
    
    const groupedTimeslots = timeslots.reduce((acc: { [key: string]: any[] }, slot: any) => {
        try {
            const startTime = new Date(slot.availableStartTime);
            if (isNaN(startTime.getTime())) {
                return acc;
            }

            const date = format(startTime, 'yyyy-MM-dd');
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(slot);
        } catch (error) {
            console.error('Error processing timeslot:', slot, error);
        }
        return acc;
    }, {});

    const canAddTimeslot = selectedDate && startTime && endTime && startTime < endTime;

    return (
        <div className="flex flex-col gap-6">
            {/* Header with status */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Clock weight="duotone" size={20} />
                    <h3 className="text-lg font-medium">Tijdslots beheren</h3>
                </div>
                <div className="text-sm text-neutral-secondary-mid">
                    Wijzigingen worden direct opgeslagen
                </div>
            </div>

            {/* Info Banner */}
            <div className="rounded-lg border border-moss-primary/20 bg-moss-primary/5 p-4">
                <div className="flex gap-3">
                    <Info size={20} weight="duotone" className="text-moss-primary shrink-0 mt-0.5" />
                    <div className="text-sm leading-relaxed text-neutral-primary">
                        <p className="font-medium text-moss-primary mb-2">Tijdslot Interval Systeem</p>
                        <p className="mb-2">
                            <strong>De duur die u hieronder selecteert wordt automatisch omgezet in intervallen van 15 minuten.</strong>
                        </p>
                        <p className="mb-2">
                            Dus als u het tijdslot instelt van 9:00 tot 10:00 dan worden er 4 tijdslots van 15 minuten gezet: 
                            <span className="font-mono bg-white px-1 mx-1 rounded-sm">9:00</span>
                            <span className="font-mono bg-white px-1 mx-1 rounded-sm">9:15</span>
                            <span className="font-mono bg-white px-1 mx-1 rounded-sm">9:30</span>
                            <span className="font-mono bg-white px-1 mx-1 rounded-sm">9:45</span>
                        </p>
                        <p className="text-neutral-secondary-mid">
                            Elk tijdslot kan door 3 personen worden ingeboekt. Dus 3 per tijdslot van 15 minuten = in totaal kunnen 12 personen per uur het appartement bezoeken.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Add New Timeslot Card */}
                <Card className="border border-neutral-primary/10">
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <Plus size={18} weight="duotone" />
                            <h4 className="font-medium">Nieuw tijdslot toevoegen</h4>
                        </div>
                    </CardHeader>
                    <CardBody className="flex flex-col gap-4">
                        <Calendar
                            value={selectedDate as any}
                            onChange={setSelectedDate}
                            minValue={today(getLocalTimeZone())}
                            classNames={{
                                base: "w-full",
                                content: "w-full"
                            }}
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <Select
                                label="Start tijd"
                                placeholder="Selecteer start"
                                selectedKeys={[startTime]}
                                onChange={(e) => setStartTime(e.target.value)}
                                size="sm"
                                items={timeOptions.map((time) => ({
                                    key: time,
                                    label: time
                                }))}
                            >
                                {(time) => (
                                    <SelectItem key={time.key}>
                                        {time.label}
                                    </SelectItem>
                                )}
                            </Select>

                            <Select
                                label="Eind tijd"
                                placeholder="Selecteer eind"
                                selectedKeys={[endTime]}
                                onChange={(e) => setEndTime(e.target.value)}
                                size="sm"
                                items={timeOptions.map((time) => ({
                                    key: time,
                                    label: time
                                }))}
                            >
                                {(time) => (
                                    <SelectItem key={time.key}>
                                        {time.label}
                                    </SelectItem>
                                )}
                            </Select>
                        </div>

                        <Button
                            color="success"
                            startContent={
                                isSaving ? (
                                    <CircularProgress size="sm" color="success" />
                                ) : (
                                    <Check weight="bold" />
                                )
                            }
                            onClick={handleAddTimeslot}
                            isDisabled={!canAddTimeslot || isSaving}
                            isLoading={isSaving}
                        >
                            {isSaving ? 'Toevoegen...' : 'Tijdslot toevoegen'}
                        </Button>

                        {!canAddTimeslot && selectedDate && (
                            <div className="flex items-center gap-2 text-xs text-brick-primary">
                                <X size={14} />
                                <span>Zorg dat eindtijd na starttijd ligt</span>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Existing Timeslots Card */}
                <Card className="border border-neutral-primary/10">
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <Clock size={18} weight="duotone" />
                            <h4 className="font-medium">Bestaande tijdslots</h4>
                        </div>
                    </CardHeader>
                    <CardBody>
                        {isLoading ? (
                            <div className="flex justify-center p-8">
                                <CircularProgress color="primary" />
                            </div>
                        ) : Object.keys(groupedTimeslots).length === 0 ? (
                            <div className="text-center p-8 text-neutral-secondary-mid">
                                <Clock size={32} className="mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Nog geen tijdslots toegevoegd</p>
                            </div>
                        ) : (
                            <div className="max-h-96 overflow-y-auto space-y-4">
                                {Object.entries(groupedTimeslots)
                                    .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
                                    .map(([date, slots]) => {
                                    const typedSlots = slots as Array<{
                                        id: number;
                                        availableStartTime: string;
                                        availableEndTime: string;
                                    }>;

                                    return (
                                        <div key={date} className="border-b border-neutral-primary/10 pb-4 last:border-b-0">
                                            <h5 className="mb-3 font-medium text-sm">
                                                {format(new Date(date), 'EEEE d MMMM yyyy')}
                                            </h5>
                                            <div className="space-y-2">
                                                {typedSlots
                                                    .filter(slot => {
                                                        const startTime = new Date(slot.availableStartTime);
                                                        const endTime = new Date(slot.availableEndTime);
                                                        return !isNaN(startTime.getTime()) && !isNaN(endTime.getTime());
                                                    })
                                                    .sort((a, b) => {
                                                        const startTimeA = new Date(a.availableStartTime);
                                                        const startTimeB = new Date(b.availableStartTime);
                                                        return startTimeA.getTime() - startTimeB.getTime();
                                                    })
                                                    .map((slot) => {
                                                        const startTime = new Date(slot.availableStartTime);
                                                        const endTime = new Date(slot.availableEndTime);

                                                        return (
                                                            <div key={slot.id} className="flex items-center justify-between p-2 rounded-sm bg-neutral-primary/5">
                                                                <div className="flex items-center gap-2">
                                                                    <Clock size={14} className="text-neutral-secondary-mid" />
                                                                    <span className="text-sm font-medium">
                                                                        {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
                                                                    </span>
                                                                </div>
                                                                <Button
                                                                    isIconOnly
                                                                    color="danger"
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => handleDeleteTimeslot(slot)}
                                                                    isLoading={isDeleting === slot.id}
                                                                >
                                                                    <Trash weight="bold" size={14} />
                                                                </Button>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>

            <PlannedVisitsWarningModal
                isOpen={!!pendingDeletion}
                visits={pendingDeletion?.visits ?? []}
                action="delete-timeslot"
                isProcessing={isDeleting !== null}
                onCancel={() => setPendingDeletion(null)}
                onConfirm={handleConfirmDeletion}
            />
        </div>
    );
};

export default ImprovedTimeslotManager;
